import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import {
  getOrgDocuments,
  getOrganisations,
  createOrgDocument,
  updateOrgDocument,
  deleteOrgDocument,
  uploadOrgDocumentFile,
  getOrgDocumentFileUrl,
  deleteOrgDocumentFile,
} from '../api';
import EmptyState from '../components/EmptyState';

export default function OrgDocumentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = searchParams.get('orgId');

  const [organisations, setOrganisations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | recordId
  const [form, setForm] = useState({ type: 'CERTIFICATION', title: '', description: '', expiryDate: '' });
  const [saving, setSaving] = useState(false);

  // If no orgId, fetch orgs so the user can pick one (or auto-select if only one)
  useEffect(() => {
    if (orgId) return;
    setOrgsLoading(true);
    getOrganisations()
      .then((orgs) => {
        setOrganisations(orgs);
        if (orgs.length === 1) {
          setSearchParams({ orgId: String(orgs[0].id) }, { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => setOrgsLoading(false));
  }, [orgId, setSearchParams]);

  const loadRecords = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    setError('');
    getOrgDocuments(orgId)
      .then(setRecords)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const startNew = () => {
    setEditing('new');
    setForm({ type: 'CERTIFICATION', title: '', description: '', expiryDate: '' });
  };

  const startEdit = (record) => {
    setEditing(record.id);
    setForm({
      type: record.type || 'CERTIFICATION',
      title: record.title || '',
      description: record.description || '',
      expiryDate: record.expiryDate || '',
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ type: 'CERTIFICATION', title: '', description: '', expiryDate: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        organisationId: Number(orgId),
        type: form.type,
        title: form.title,
        description: form.description || null,
        expiryDate: form.expiryDate || null,
      };
      if (editing === 'new') {
        await createOrgDocument(payload);
      } else {
        await updateOrgDocument(editing, payload);
      }
      cancelEdit();
      loadRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document record?')) return;
    setError('');
    try {
      await deleteOrgDocument(id);
      loadRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFileUpload = async (recordId, file) => {
    setError('');
    try {
      await uploadOrgDocumentFile(recordId, file);
      loadRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFileDownload = async (recordId) => {
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getOrgDocumentFileUrl(recordId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to download file');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'document-file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFileDelete = async (recordId) => {
    if (!window.confirm('Remove this file attachment?')) return;
    setError('');
    try {
      await deleteOrgDocumentFile(recordId);
      loadRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!orgId) {
    return (
      <div className="page-container">
        <div className="form-card">
          <h2>Certifications & Insurance</h2>
          {orgsLoading ? (
            <p>Loading organisations...</p>
          ) : organisations.length === 0 ? (
            <p>No organisations found.</p>
          ) : (
            <>
              <p style={{ marginBottom: '0.75rem', color: '#666', fontSize: '0.9rem' }}>Select an organisation to view its documents:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {organisations.map((o) => (
                  <button
                    key={o.id}
                    className="btn btn-outline"
                    style={{ textAlign: 'left', padding: '0.75rem 1rem' }}
                    onClick={() => setSearchParams({ orgId: String(o.id) }, { replace: true })}
                  >
                    {o.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Documents' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>Certifications & Insurance</h2>
          {editing === null && (
            <button className="btn btn-primary" onClick={startNew}>Add Record</button>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {editing !== null && (
          <form onSubmit={handleSave} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb' }}>
            <h3 style={{ marginTop: 0 }}>{editing === 'new' ? 'New Record' : 'Edit Record'}</h3>
            <div className="form-group">
              <label>Type *</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} required>
                <option value="CERTIFICATION">Certification</option>
                <option value="INSURANCE">Insurance</option>
              </select>
            </div>
            <div className="form-group">
              <label>Title *</label>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="form-group">
              <label>Expiry Date</label>
              <input type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" className="btn btn-outline" onClick={cancelEdit}>Cancel</button>
            </div>
          </form>
        )}

        {loading && <p>Loading...</p>}

        {!loading && records.length > 0 && (
          <div className="table-scroll">
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Expiry</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>File</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{r.type}</td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{r.title}</td>
                    <td style={{
                      padding: '0.5rem',
                      borderBottom: '1px solid #e5e7eb',
                      color: r.isExpired ? '#dc2626' : 'inherit',
                      fontWeight: r.isExpired ? 'bold' : 'normal',
                    }}>
                      {r.expiryDate || '-'}
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                      {r.filePath ? (
                        <span style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem' }} onClick={() => handleFileDownload(r.id)}>Download</button>
                          <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', color: '#dc2626' }} onClick={() => handleFileDelete(r.id)}>Remove</button>
                        </span>
                      ) : (
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                          style={{ fontSize: '0.8rem' }}
                          onChange={(e) => {
                            if (e.target.files[0]) handleFileUpload(r.id, e.target.files[0]);
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem' }} onClick={() => startEdit(r)}>Edit</button>
                        <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', color: '#dc2626' }} onClick={() => handleDelete(r.id)}>Delete</button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && records.length === 0 && !error && <EmptyState message="No certifications or insurance records found." />}
      </div>
    </div>
  );
}
